import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import type { ErrorCode, IssueDetail, IssueFieldOptions, IssueSummary } from '@jisy/shared';
import { ApiRequestError } from '../api/client.js';
import { issueChildrenQuery, useIssueConvert, useIssueUpdate } from '../api/queries.js';
import { ParentPicker } from './ParentPicker.js';
import styles from './IssueHierarchy.module.css';

type Mode = 'idle' | 'change' | 'toSubtask' | 'toTask';

/**
 * Родитель в боковой колонке карточки — поле «Parent» из Jira, и здесь же смена уровня.
 *
 * Подзадача переезжает к другой задаче обычной правкой. Задача ⇄ подзадача — уже перенос:
 * Jira выполняет его фоновым заданием и сама подбирает статус, если у подзадач и задач разные
 * процессы, поэтому у него своё «Переносим…» и подсказка о статусе.
 *
 * Задача с подзадачами под другую задачу не уходит: подзадач у подзадач в Jira не бывает, —
 * действие для неё просто не показывается, а не отказывает после нажатия.
 */
export function IssueHierarchy({
  issue,
  issueTypes,
  onOpenIssue,
}: {
  issue: IssueDetail;
  /** Типы проекта из значений карточки; null — ещё не пришли. */
  issueTypes: IssueFieldOptions['issueTypes'] | null;
  onOpenIssue: (issueKey: string) => void;
}): React.ReactElement | null {
  const { t } = useTranslation();
  const subtask = issue.type?.subtask === true;
  const level = issue.type?.hierarchyLevel ?? 0;

  const update = useIssueUpdate(issue.key);
  const convert = useIssueConvert(issue.key);
  // Тот же запрос, что у раздела «Подзадачи»: ответ уже в кэше, второго похода в Jira нет.
  const children = useQuery(issueChildrenQuery(issue.key, !subtask));

  const [mode, setMode] = useState<Mode>('idle');
  const [parent, setParent] = useState<IssueSummary | null>(null);
  const [typeId, setTypeId] = useState('');

  const subtaskTypes = useMemo(() => (issueTypes ?? []).filter((type) => type.subtask), [issueTypes]);
  const taskTypes = useMemo(
    () => (issueTypes ?? []).filter((type) => !type.subtask && type.hierarchyLevel === 0),
    [issueTypes],
  );

  // У эпика родитель — надстройки над эпиками, которых в Jisy нет; этим полем он не управляется.
  if (level > 0) return null;

  const canToTask = subtask && taskTypes.length > 0;
  const canToSubtask =
    !subtask && children.data !== undefined && children.data.items.length === 0 && subtaskTypes.length > 0;

  // Родителя нет и сделать ничего нельзя — поле только заняло бы место.
  if (!issue.parent && !canToSubtask && !subtask) return null;

  const updated = issue.updated;
  const busy = update.isPending || convert.isPending;
  const failure = [convert.error, update.error].find(
    (error): error is ApiRequestError => error instanceof ApiRequestError,
  );

  function start(next: Mode): void {
    update.reset();
    convert.reset();
    setParent(null);
    setTypeId((next === 'toTask' ? taskTypes[0] : subtaskTypes[0])?.id ?? '');
    setMode(next);
  }

  function move(): void {
    if (!updated || typeId === '') return;
    const parentKey = mode === 'toSubtask' ? (parent?.key ?? null) : null;
    if (mode === 'toSubtask' && parentKey === null) return;
    convert.mutate({ issueTypeId: typeId, parentKey, ifUpdated: updated }, { onSuccess: () => setMode('idle') });
  }

  return (
    <div className={styles.field}>
      <span className="label">{t('issue.parent')}</span>

      {issue.parent ? (
        <button
          type="button"
          className={styles.value}
          onClick={() => issue.parent && onOpenIssue(issue.parent.key)}
          title={t('issue.openParent', { key: issue.parent.key })}
        >
          <span className={`mono ${styles.valueKey}`}>{issue.parent.key}</span>
          <span className="truncate">{issue.parent.summary}</span>
        </button>
      ) : (
        <span className={styles.empty}>—</span>
      )}

      {mode === 'idle' && (subtask || canToSubtask) && (
        <div className={styles.actions}>
          {subtask && (
            <button type="button" className={styles.link} onClick={() => start('change')}>
              {t('issue.parentChange')}
            </button>
          )}
          {canToTask && (
            <button type="button" className={styles.link} onClick={() => start('toTask')}>
              {t('issue.toTask')}
            </button>
          )}
          {canToSubtask && (
            <button type="button" className={styles.link} onClick={() => start('toSubtask')}>
              {t('issue.toSubtask')}
            </button>
          )}
        </div>
      )}

      {/* Смена родителя: выбор сразу сохраняется — подтверждать тут нечего, вернуть так же просто. */}
      {mode === 'change' &&
        (update.isPending ? (
          <span className={styles.hint}>{t('table.saving')}</span>
        ) : (
          <div className={styles.form}>
            <ParentPicker
              projectId={issue.projectId}
              value={null}
              excludeKey={issue.parent?.key}
              autoFocus
              onChange={(item) => {
                if (!item || !updated) return;
                update.mutate({ parentKey: item.key, ifUpdated: updated }, { onSuccess: () => setMode('idle') });
              }}
            />
            <div className={styles.buttons}>
              <button type="button" className={styles.secondary} onClick={() => setMode('idle')}>
                {t('issue.cancel')}
              </button>
            </div>
          </div>
        ))}

      {(mode === 'toSubtask' || mode === 'toTask') && (
        <div className={styles.form}>
          {mode === 'toSubtask' && (
            <ParentPicker
              projectId={issue.projectId}
              value={parent}
              excludeKey={issue.key}
              autoFocus
              onChange={setParent}
            />
          )}

          {(mode === 'toTask' ? taskTypes : subtaskTypes).length > 1 && (
            <select
              className={styles.select}
              value={typeId}
              onChange={(event) => setTypeId(event.target.value)}
              aria-label={t('table.columns.type')}
            >
              {(mode === 'toTask' ? taskTypes : subtaskTypes).map((type) => (
                <option value={type.id} key={type.id}>
                  {type.name}
                </option>
              ))}
            </select>
          )}

          <span className={styles.hint}>
            {t(mode === 'toTask' ? 'issue.toTaskHint' : 'issue.toSubtaskHint')}
          </span>

          <div className={styles.buttons}>
            <button
              type="button"
              className={styles.primary}
              onClick={move}
              disabled={busy || typeId === '' || (mode === 'toSubtask' && parent === null)}
            >
              {convert.isPending ? t('issue.moving') : t('issue.moveSubmit')}
            </button>
            <button
              type="button"
              className={styles.secondary}
              onClick={() => setMode('idle')}
              disabled={convert.isPending}
            >
              {t('issue.cancel')}
            </button>
          </div>
        </div>
      )}

      {failure && (
        <p className={styles.error} role="alert">
          {t(`errors.${failure.code}` satisfies `errors.${ErrorCode}`, failure.params ?? {})}
        </p>
      )}
    </div>
  );
}
